import Icon from "@bokio/designsystem/components/Icon/Icon";
import { LoadingSpinner } from "@bokio/designsystem/components/Loading";
import { mergeClassNames } from "@bokio/utils/classes";

import type { ChangeEvent, FocusEvent, InputHTMLAttributes, KeyboardEvent, MutableRefObject } from "react";

import * as styles from "./search.scss";

type InputElementAttributes = InputHTMLAttributes<HTMLInputElement>;

export interface SearchFieldProps {
	value: string;
	disabled?: boolean;
	padding?: boolean;
	placeholder?: string;
	onChange: (value: string) => void;
	doSearch?: (value: string) => void;
	allowClear?: boolean;
	onClearSearch?: () => void;
	wide?: boolean;
	autoFocus?: boolean;
	testId?: string;
	className?: string;
	onFocus?: () => void;
	onBlur?: (e: FocusEvent) => void;
	inputRef?: MutableRefObject<HTMLInputElement | null>;
	/**
	 * Additional props to the actual input element.
	 */
	fieldProps?: InputElementAttributes;
	/**
	 * Whether to show the looking glass icon. `true` by default.
	 */
	showIcon?: boolean;
	loading?: boolean;
}

export const SearchField = ({
	value,
	disabled,
	padding,
	placeholder,
	onChange,
	doSearch,
	allowClear,
	onClearSearch,
	wide,
	autoFocus,
	testId,
	className,
	onFocus,
	onBlur,
	inputRef,
	fieldProps,
	showIcon = true,
	loading,
}: SearchFieldProps) => {
	const handleDoSearch = () => {
		if (doSearch) {
			doSearch(value);
		}
	};

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		onChange(e.target.value);
	};

	const handleKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter" && (value || value === "")) {
			handleDoSearch();
		}
	};

	return (
		<div className={padding ? styles.padding : undefined}>
			<div className={mergeClassNames(className, wide ? styles.searchWide : styles.search)}>
				<input
					{...fieldProps}
					ref={inputRef}
					data-testid={testId}
					type="search"
					autoFocus={autoFocus}
					onChange={handleChange}
					value={value || ""}
					disabled={disabled}
					placeholder={placeholder}
					onKeyUp={handleKeyUp}
					onFocus={onFocus}
					onBlur={onBlur}
				/>

				{loading && (
					<div>
						<div className={mergeClassNames(styles.icon, styles.spinner)}>
							<LoadingSpinner size="small" />
						</div>
					</div>
				)}
				{showIcon &&
					!loading &&
					(allowClear && onClearSearch && value ? (
						<div onClick={onClearSearch}>
							<Icon name="cancel" color="darkGrey" className={styles.icon} size={"24"} />
						</div>
					) : (
						<div onClick={handleDoSearch}>
							<Icon
								name="search"
								color="darkGrey"
								className={styles.icon}
								size={"24"}
								testId="SearchField_HourglassIcon"
							/>
						</div>
					))}
			</div>
		</div>
	);
};
